import { z } from 'zod';

/**
 * Single source of truth for the media-unit cost-record range-filter
 * dimensions (CU-86cataqt9). Every displayed cost column that backs a
 * `<id>_min`/`<id>_max` range filter is listed here exactly once, together
 * with its `media_unit_costs` column name.
 *
 * Consumers that previously hand-duplicated this list:
 * - `MediaUnitFiltersSchema`'s min/max fields (media-units.ts) — derives its
 *   shape from here.
 * - `OfferUnitFiltersSchema`'s min/max fields (offer-unit.ts) — derives its
 *   shape from here.
 * - Both schemas' superRefine inverted-range check reuses `validateRangeFieldPairs`
 *   below instead of hand-rolling the loop (single source for both the base
 *   `[area, width, height, audience]` pairs AND the validation behavior).
 * - `COST_RANGE_FILTER_KEYS` in apps/api/src/media-units/media-units.service.ts
 *   (the cost-filter strip set) — derives from here, intersected with that
 *   file's own INTERNAL_COST_FIELDS/LIST_INTERNAL_COST_FIELDS (the response
 *   field-visibility source of truth) to decide which dimensions to strip.
 * - The 14 hand-written `buildCostRangeCondition` blocks in
 *   packages/db/src/queries/media-units/build-filter-conditions.ts — replaced
 *   by a single generic loop over this list.
 * - `RANGE_FIELD_DEFS` in apps/web/src/lib/range-field-defs.ts
 *   — derives its cost-dimension entries from here.
 *
 * Adding a new cost-record range filter means adding ONE entry here.
 */
export const COST_RANGE_DIMENSIONS = [
  { id: 'catalogPrice', column: 'catalog_price' },
  { id: 'rent', column: 'rent' },
  { id: 'exposureCost', column: 'exposure_cost' },
  { id: 'additionalCosts', column: 'additional_costs' },
  { id: 'discountedPrice', column: 'discounted_price' },
  { id: 'lastMinutePrice', column: 'last_minute_price' },
  { id: 'starterPrice', column: 'starter_price' },
  { id: 'productionCost', column: 'production_cost' },
  { id: 'installationCost', column: 'installation_cost' },
  { id: 'dismantlingCost', column: 'dismantling_cost' },
  { id: 'clientProduction', column: 'client_production' },
  { id: 'clientInstallation', column: 'client_installation' },
  { id: 'clientDismantling', column: 'client_dismantling' },
  { id: 'clientAdditionalFees', column: 'client_additional_fees' },
] as const satisfies ReadonlyArray<{ id: string; column: string }>;

export type CostRangeDimension = (typeof COST_RANGE_DIMENSIONS)[number];
export type CostRangeDimensionId = CostRangeDimension['id'];

/** `[<id>_min, <id>_max]` key pairs for every cost dimension, in declaration order. */
export const COST_RANGE_FIELD_PAIRS = COST_RANGE_DIMENSIONS.map(
  (d) => [`${d.id}_min`, `${d.id}_max`] as const,
);

/**
 * `rent_min`/`rent_max` filter keys specifically — shared by the catalogue's
 * and the offer table's separate rent-range filter guards, both of which
 * require the additional wildcard `cost.view_rent` grant on top of
 * `cost.view` (field-visibility's cost-gating requirement,
 * rent-cost-visibility-permission). `satisfies CostRangeDimensionId` ties
 * this literal to `rent` staying a member of `COST_RANGE_DIMENSIONS`, so
 * dropping that entry is a compile error here rather than a silent drift
 * between the two guards' independently hand-rolled copies.
 */
const RENT_DIMENSION_ID = 'rent' as const satisfies CostRangeDimensionId;
export const RENT_RANGE_FILTER_KEYS = [
  `${RENT_DIMENSION_ID}_min`,
  `${RENT_DIMENSION_ID}_max`,
] as const;

/**
 * `<id>_min`/`<id>_max` key pairs for the 4 base (non-cost) range dimensions
 * shared by every media-unit-derived filter schema (`MediaUnitFiltersSchema`
 * in media-units.ts, `OfferUnitFiltersSchema` in offer-unit.ts).
 */
export const BASE_RANGE_FIELD_PAIRS = [
  ['area_min', 'area_max'],
  ['width_min', 'width_max'],
  ['height_min', 'height_max'],
  ['audience_min', 'audience_max'],
] as const;

/**
 * Every `<id>_min`/`<id>_max` key pair on a media-unit-derived filter schema
 * (base dims + COST_RANGE_DIMENSIONS) — the loop domain for
 * `validateRangeFieldPairs` below.
 */
export const RANGE_FIELD_PAIRS = [...BASE_RANGE_FIELD_PAIRS, ...COST_RANGE_FIELD_PAIRS] as const;

/**
 * Shared `.superRefine()` body: rejects an inverted numeric range (max < min)
 * for every pair in RANGE_FIELD_PAIRS. A single bound (one-sided range) is
 * valid; a pair is rejected only when BOTH bounds are present and max < min.
 * Used by both `MediaUnitFiltersSchema` (media-units.ts) and
 * `OfferUnitFiltersSchema` (offer-unit.ts) so a future change to the base
 * range dimensions is applied in exactly one place.
 */
export function validateRangeFieldPairs(
  value: Record<string, unknown>,
  ctx: z.RefinementCtx,
): void {
  for (const [minKey, maxKey] of RANGE_FIELD_PAIRS) {
    const min = value[minKey] as number | undefined;
    const max = value[maxKey] as number | undefined;
    if (min !== undefined && max !== undefined && max < min) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `${maxKey} must not be less than ${minKey}`,
        path: [maxKey],
      });
    }
  }
}
